import React, { createContext, useContext, useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { RoadmapTemplate, User } from '@/types/roadmap';
import { RoadmapContextType } from './types';
import { initialUsers } from './initialData';
import {
  fetchTemplates,
  addTemplate as addTemplateAction,
  updateTemplate as updateTemplateAction,
  deleteTemplate as deleteTemplateAction,
  getTemplate as getTemplateAction
} from './templateOperations';
import { login as loginAction, logout as logoutAction } from './userOperations';
import { toast } from '@/hooks/use-toast';
import { generateUUID } from '@/utils/generateUUID';
import { useAuth } from '@/contexts/auth';
import { useWidget } from '@/contexts/WidgetContext';

// Create context with default values
const RoadmapContext = createContext<RoadmapContextType | undefined>(undefined);

export const RoadmapProvider = ({ children }: { children: React.ReactNode }) => { // Changed to non-FC export
  const [templates, setTemplates] = useState<RoadmapTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [users] = useState<User[]>(initialUsers);

  // Use auth from AuthProvider instead of managing our own auth state
  const { currentUser: authUser } = useAuth();
  const { companyId: widgetCompanyId } = useWidget();

  // Add React Query client for cache invalidation
  const queryClient = useQueryClient();

  // Convert auth user to roadmap user format
  const currentUser: User | null = authUser ? {
    id: authUser.id,
    username: authUser.name || 'user',
    isAdmin: authUser.isAdmin,
    email: authUser.name || '',
    name: authUser.name
  } : null;

  // Fetch templates from Supabase when user changes
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // Load templates
        const templateData = await fetchTemplates();
        setTemplates(templateData);
      } catch (error) {
        console.error("Error loading data:", error);
        toast({
          title: "Failed to load data",
          description: "Please try again.",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [currentUser?.id, widgetCompanyId]); // Only depend on user ID to avoid object reference issues

  const addTemplate = async (template: Omit<RoadmapTemplate, 'id'>) => {
    try {
      if (!currentUser) {
        throw new Error('You must be logged in to add a template.');
      }

      const newId = await addTemplateAction(template);
      const newTemplate = await getTemplateAction(newId);

      if (newTemplate) {
        setTemplates(prev => [...prev, newTemplate]);
        toast({
          title: "Template created",
          description: `Successfully created template "${template.title}"`
        });
      }

      return newId;
    } catch (error) {
      console.error("Error in addTemplate:", error);
      toast({
        title: "Failed to create template",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive"
      });
      throw error;
    }
  };

  const updateTemplate = async (template: RoadmapTemplate) => {
    try {
      if (!currentUser) {
        toast({
          title: "Authentication required",
          description: "You must be logged in to update a template",
          variant: "destructive"
        });
        return;
      }

      await updateTemplateAction(template);

      queryClient.invalidateQueries({ queryKey: ['roadmap', template.id] });

      const updatedTemplate = await getTemplateAction(template.id);

      if (updatedTemplate) {
        setTemplates(prev => prev.map(t =>
          t.id === template.id ? updatedTemplate : t
        ));

        toast({
          title: "Template updated",
          description: `Successfully updated template "${template.title}"`
        });
      }
    } catch (error) {
      console.error("Error in updateTemplate:", error);
      toast({
        title: "Failed to update template",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive"
      });
      throw error;
    }
  };

  const deleteTemplate = async (id: string) => {
    try {
      if (!currentUser) {
        toast({
          title: "Authentication required",
          description: "You must be logged in to delete a template",
          variant: "destructive"
        });
        return;
      }

      await deleteTemplateAction(id);
      setTemplates(prev => prev.filter(template => template.id !== id));

      queryClient.invalidateQueries({ queryKey: ['roadmap', id] });

      toast({
        title: "Template deleted",
        description: "The template has been successfully deleted"
      });
    } catch (error) {
      console.error("Error in deleteTemplate:", error);
      toast({
        title: "Failed to delete template",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive"
      });
      throw error;
    }
  };

  const getTemplate = async (id: string) => {
    try {
      console.log(`🔍 RoadmapContext.getTemplate: Fetching fresh template for ${id}`);

      const freshTemplate = await getTemplateAction(id);

      if (freshTemplate) {
        console.log(`✅ RoadmapContext.getTemplate: Fresh template loaded for ${id}`);
        return freshTemplate;
      }

      // Only use cached as fallback if fresh fetch fails
      const existingTemplate = templates.find(t => t.id === id);
      if (existingTemplate) {
        console.log(`⚠️ RoadmapContext.getTemplate: Using cached template for ${id} (fresh fetch failed)`);
      }

      return existingTemplate;
    } catch (error) {
      console.error("Error in getTemplate:", error);
      toast({
        title: "Failed to load template",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive"
      });
      return null;
    }
  };

  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      return await loginAction(username, password);
    } catch (error) {
      console.error("Error in login:", error);
      return false;
    }
  };

  const logout = async () => {
    try {
      await logoutAction();
      toast({
        title: "Logout successful",
        description: "You have been logged out"
      });
    } catch (error) {
      console.error("Error in logout:", error);
      toast({
        title: "Logout error",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive"
      });
    }
  };

  const refreshTemplates = async () => {
    try {
      const templateData = await fetchTemplates();
      setTemplates(templateData);
    } catch (error) {
      console.error("Error refreshing templates:", error);
    }
  };

  return (
    <RoadmapContext.Provider
      value={{
        templates,
        addTemplate,
        updateTemplate,
        deleteTemplate,
        getTemplate,
        refreshTemplates,
        currentUser,
        login,
        logout
      }}
    >
      {!loading && children}
    </RoadmapContext.Provider>
  );
};

export const useRoadmap = () => {
  const context = useContext(RoadmapContext);
  if (context === undefined) {
    throw new Error('useRoadmap must be used within a RoadmapProvider');
  }
  return context;
};
